import { ArrowRight, Check, Clock, FileText, Globe, MapPin, Sparkles, TrendingUp, Video } from "lucide-react";
import { motion } from "motion/react";
import { DEMO_URL, REGISTER_URL } from "../constants";

const pills = [
	{ icon: FileText, label: "Proposals & quotes" },
	{ icon: Clock, label: "Save 10+ hrs/week" },
	{ icon: MapPin, label: "30+ countries" },
	{ icon: Video, label: "Travel reels" },
	{ icon: Globe, label: "Trip pages" },
	{ icon: TrendingUp, label: "3× faster replies" },
];

const guarantees = ["Cancel anytime, no questions asked", "Unlimited team seats on all plans", "Onboarding support included"];

const bottomStats = [
	{ value: "1.5M+", label: "trips built" },
	{ value: "30+", label: "countries served" },
	{ value: "4.9★", label: "avg. rating" },
	{ value: "10 hrs", label: "saved per week" },
];

export function CTABlock() {
	return (
		<section className="relative px-6 pb-20" style={{ backgroundColor: "#FAFBFC" }}>
			<div className="max-w-5xl mx-auto">
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className="rounded-3xl overflow-hidden relative"
					style={{
						background: "#1a093b",
						boxShadow: "0 24px 64px rgba(26,9,59,0.45)",
					}}
				>
					{/* Ambient glow */}
					<div
						className="absolute pointer-events-none"
						style={{
							top: -60,
							right: -60,
							width: 340,
							height: 340,
							borderRadius: "50%",
							background: "radial-gradient(circle, rgba(140,93,241,0.18) 0%, transparent 70%)",
							filter: "blur(20px)",
						}}
					/>
					<div
						className="absolute pointer-events-none"
						style={{
							bottom: 0,
							left: -40,
							width: 260,
							height: 260,
							borderRadius: "50%",
							background: "radial-gradient(circle, rgba(140,93,241,0.09) 0%, transparent 70%)",
							filter: "blur(16px)",
						}}
					/>

					{/* Main content */}
					<div className="relative grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-10 px-5 md:px-10 pt-10 pb-8 items-start">
						{/* Left */}
						<div className="flex flex-col">
							{/* Eyebrow pill */}
							<div
								className="inline-flex items-center gap-1.5 self-start px-3 py-1 rounded-full mb-5"
								style={{
									background: "rgba(140,93,241,0.15)",
									border: "1px solid rgba(140,93,241,0.25)",
								}}
							>
								<Sparkles style={{ width: 10, height: 10, color: "#8c5df1" }} />
								<span
									style={{
										color: "#8c5df1",
										fontSize: 11,
										fontWeight: 600,
										letterSpacing: "0.12em",
										textTransform: "uppercase",
									}}
								>
									For travel professionals
								</span>
							</div>

							{/* Headline */}
							<h2
								className="font-bold mb-4"
								style={{
									fontFamily: "Poppins, sans-serif",
									fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
									lineHeight: 1.15,
									color: "#ffffff",
								}}
							>
								Build proposals.
								<br />
								<span style={{ color: "#8c5df1" }}>Close deals.</span>
								<br />
								Market with ease.
							</h2>

							{/* Body */}
							<p
								className="mb-7"
								style={{
									color: "rgba(255,255,255,0.42)",
									fontSize: 14,
									lineHeight: 1.72,
									maxWidth: 420,
								}}
							>
								Everything your travel business needs: polished proposals, instant quotes, client-ready itineraries, and a branded presence that wins trust
								before the first call.
							</p>

							{/* Pills */}
							<div className="flex flex-wrap gap-2">
								{pills.map((pill) => {
									const Icon = pill.icon;
									return (
										<div
											key={pill.label}
											className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
											style={{
												background: "rgba(255,255,255,0.05)",
												border: "1px solid rgba(255,255,255,0.08)",
												color: "rgba(255,255,255,0.55)",
												fontSize: 12,
												fontWeight: 500,
											}}
										>
											<Icon style={{ width: 11, height: 11, color: "#8c5df1", flexShrink: 0 }} />
											{pill.label}
										</div>
									);
								})}
							</div>
						</div>

						{/* Right — signup card + guarantees */}
						<div className="flex flex-col gap-3">
							<div
								className="rounded-2xl p-5 flex flex-col gap-4"
								style={{
									background: "rgba(140,93,241,0.07)",
									border: "1px solid rgba(140,93,241,0.22)",
									boxShadow: "0 0 28px rgba(140,93,241,0.1) inset",
								}}
							>
								{/* Card headline */}
								<div className="text-center">
									<p
										className="font-bold mb-1"
										style={{
											fontFamily: "Poppins, sans-serif",
											color: "#ffffff",
											fontSize: 18,
										}}
									>
										Start building today
									</p>
									<p
										style={{
											color: "rgba(255,255,255,0.38)",
											fontSize: 12.5,
											lineHeight: 1.55,
										}}
									>
										7 days free · No credit card
										<br />
										Full access from day one
									</p>
								</div>

								{/* Primary CTA */}
								<motion.a
									href={REGISTER_URL}
									target="_blank"
									rel="noopener noreferrer"
									whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(140,93,241,0.55)" }}
									whileTap={{ scale: 0.97 }}
									className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold"
									style={{
										background: "linear-gradient(135deg, #8c5df1 0%, #6d3fd4 100%)",
										color: "#ffffff",
										fontSize: 14,
										boxShadow: "0 4px 20px rgba(140,93,241,0.38)",
										transition: "box-shadow 0.2s",
									}}
								>
									Start free trial
									<ArrowRight style={{ width: 14, height: 14 }} />
								</motion.a>

								{/* Secondary */}
								<a
									href={DEMO_URL}
									target="_blank"
									rel="noopener noreferrer"
									className="text-center block"
									style={{
										color: "rgba(255,255,255,0.38)",
										fontSize: 12.5,
										fontWeight: 500,
										textDecoration: "underline",
										textDecorationColor: "rgba(255,255,255,0.15)",
										textUnderlineOffset: 3,
										transition: "color 0.2s",
									}}
								>
									Book a demo instead
								</a>
							</div>

							{/* Guarantees */}
							<div className="flex flex-col gap-1.5 px-1 pt-1">
								{guarantees.map((g) => (
									<div key={g} className="flex items-center gap-2">
										<Check style={{ width: 12, height: 12, color: "#8c5df1", flexShrink: 0 }} />
										<span style={{ color: "rgba(255,255,255,0.38)", fontSize: 12 }}>{g}</span>
									</div>
								))}
							</div>
						</div>
					</div>

					{/* Stats bar */}
					<div
						className="relative px-5 md:px-10 py-3.5 flex flex-wrap items-center justify-between gap-3"
						style={{
							borderTop: "1px solid rgba(255,255,255,0.06)",
							background: "rgba(0,0,0,0.18)",
						}}
					>
						<div className="grid grid-cols-2 lg:flex items-center gap-x-1 gap-y-1.5">
							{bottomStats.map((s, i) => (
								<div key={s.label} className="flex items-center">
									{i > 0 && (
										<span className="hidden lg:inline-block" style={{ color: "rgba(255,255,255,0.12)", margin: "0 14px" }}>
											·
										</span>
									)}
									<span
										style={{
											fontFamily: "Poppins, sans-serif",
											fontWeight: 700,
											fontSize: 14,
											color: "#ffffff",
											marginRight: 4,
										}}
									>
										{s.value}
									</span>
									<span style={{ color: "rgba(255,255,255,0.32)", fontSize: 12 }}>{s.label}</span>
								</div>
							))}
						</div>
						<span style={{ color: "rgba(255,255,255,0.2)", fontSize: 12, fontStyle: "italic" }}>Travel starts with Stippl.</span>
					</div>
				</motion.div>
			</div>
		</section>
	);
}
